import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { createGovernorates } from "../../../../../services/Admin/ShippingCategoriesApi";
import { getShippingCategories } from "../../../../../services/Admin/ShippingCategoriesApi";

const emptyGovernorate = { name_ar: "", name_en: "", status: true, shipping_category_id: "" };

const AddGovernoratesDialog = ({ open, onClose, onSuccess, categoryId }) => {
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [governorates, setGovernorates] = useState([{ ...emptyGovernorate, shipping_category_id: categoryId || "" }]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      fetchCategories();
      resetForm();
    }
  }, [open, categoryId]);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await getShippingCategories();
      const data = res?.success ? res.data : res;
      const allCategories = [];
      if (Array.isArray(data)) {
        data.forEach((group) => {
          if (group.categories) {
            allCategories.push(...group.categories);
          }
        });
      }
      setCategories(allCategories);
    } catch (err) {
      console.error("Failed to load categories", err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const resetForm = () => {
    setGovernorates([{ ...emptyGovernorate, shipping_category_id: categoryId || "" }]);
    setError("");
  };

  const handleAddGovernorate = () => {
    setGovernorates([...governorates, { ...emptyGovernorate, shipping_category_id: categoryId || "" }]);
  };

  const handleRemoveGovernorate = (index) => {
    if (governorates.length > 1) {
      setGovernorates(governorates.filter((_, i) => i !== index));
    }
  };

  const handleGovernorateChange = (index, field, value) => {
    const updated = [...governorates];
    updated[index][field] = value;
    setGovernorates(updated);
  };

  const handleSubmit = async () => {
  setError("");

  const validGovernorates = governorates.filter((g) => g.name_ar.trim() || g.name_en.trim());
  if (validGovernorates.length === 0) {
    setError("يرجى إضافة محافظة واحدة على الأقل");
    return;
  }

  // ✅ array فقط (الدالة هي اللي هتعمل wrapper)
  const govArray = validGovernorates.map((g) => ({
    name_ar: g.name_ar.trim(),
    name_en: g.name_en.trim(),
    status: !!g.status,
    shipping_category_id: g.shipping_category_id ? Number(g.shipping_category_id) : null,
  }));

  setLoading(true);
  try {
    const res = await createGovernorates(govArray); // ✅
    if (res?.success) {
      onSuccess?.();
      onClose?.();
    } else {
      setError(res?.message || "فشل إضافة المحافظات");
    }
  } catch (err) {
    setError(err?.message || "حدث خطأ أثناء الإضافة");
  } finally {
    setLoading(false);
  }
};


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>إضافة محافظات</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">المحافظات</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddGovernorate}>
                إضافة محافظة
              </Button>
            </Stack>

            <Stack spacing={2}>
              {governorates.map((gov, index) => (
                <Paper key={index} sx={{ p: 2 }} variant="outlined">
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        label="الاسم بالعربية"
                        value={gov.name_ar}
                        onChange={(e) => handleGovernorateChange(index, "name_ar", e.target.value)}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label="الاسم بالإنجليزية"
                        value={gov.name_en}
                        onChange={(e) => handleGovernorateChange(index, "name_en", e.target.value)}
                        size="small"
                        fullWidth
                      />
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveGovernorate(index)}
                        disabled={governorates.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>شريحة الشحن</InputLabel>
                        <Select
                          value={gov.shipping_category_id}
                          label="شريحة الشحن"
                          onChange={(e) => handleGovernorateChange(index, "shipping_category_id", e.target.value)}
                          disabled={categoriesLoading || !!categoryId}
                        >
                          <MenuItem value="">بدون شريحة</MenuItem>
                          {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name} - {Number(cat.cost).toFixed(2)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={gov.status}
                            onChange={(e) => handleGovernorateChange(index, "status", e.target.checked)}
                          />
                        }
                        label={gov.status ? "مفعّل" : "معطّل"}
                      />
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          إلغاء
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "حفظ"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddGovernoratesDialog;
